import { useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

import { humanizeLabel } from "../data/alert-queue";
import {
  fetchShiftResponseLog,
  SHIFT_RESPONSE_LOG_ROW_LIMIT,
  type ShiftResponseLogRow,
} from "../data/schedule";
import { useReadModel } from "../data/useReadModel";
import { formatDisplayTime } from "../lib/display-time";

// Read-only supervisor report over public.management_shift_response_log. The view redacts
// people to labels and exposes latest response state only; this surface intentionally has no
// acknowledge/decline/reassign controls.
//
// Presented as stacked response cards (not a six-column table) so Guard through Requirements
// stay readable on Coverage's full-width pane and at narrow Console widths.

const RESPONSE_FILTERS = ["all", "acknowledged", "declined"] as const;

function responseChipClass(status: string): string {
  if (status === "acknowledged") {
    return "schedule__chip schedule__chip--filled";
  }
  if (status === "declined") {
    return "schedule__chip schedule__chip--at-risk";
  }
  return "schedule__chip schedule__chip--draft";
}

function formatClock(iso: string, timeZone: string): string {
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone,
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short",
    }).format(new Date(iso));
  } catch {
    return formatDisplayTime(iso, timeZone);
  }
}

function formatWindow(row: ShiftResponseLogRow, timeZone: string): string {
  if (!row.startsAt && !row.endsAt) {
    return "-";
  }
  if (row.startsAt && row.endsAt) {
    return `${formatClock(row.startsAt, timeZone)} to ${formatClock(row.endsAt, timeZone)}`;
  }
  return `${row.startsAt ? formatClock(row.startsAt, timeZone) : "-"} to ${
    row.endsAt ? formatClock(row.endsAt, timeZone) : "-"
  }`;
}

function shiftSummary(row: ShiftResponseLogRow, timeZone: string): string {
  const window = formatWindow(row, timeZone);
  if (row.slotDate && window !== "-") {
    return `${row.slotDate} · ${window}`;
  }
  return row.slotDate ?? window;
}

function routeSummary(row: ShiftResponseLogRow): string {
  if (row.routeName && row.siteName) {
    return `${row.routeName} · ${row.siteName}`;
  }
  return row.routeName ?? row.siteName ?? "-";
}

export function ShiftResponseReport({
  client,
  accountId,
  timeZone,
  pollMs = 30000,
}: {
  client: SupabaseClient;
  accountId: string;
  timeZone: string;
  pollMs?: number;
}) {
  const { status, data, error } = useReadModel<ShiftResponseLogRow[]>(
    () => fetchShiftResponseLog(client, accountId),
    [client, accountId],
    pollMs,
  );
  const [responseFilter, setResponseFilter] = useState<string>("all");

  if (status === "loading" && data === null) {
    return <p className="surface__state" role="status">Loading shift responses...</p>;
  }
  if (status === "error") {
    return (
      <p className="surface__state surface__state--error" role="alert">
        {error ?? "Could not load shift responses."}
      </p>
    );
  }

  const rows = data ?? [];
  if (rows.length === 0) {
    return (
      <p className="surface__state" role="status">
        No shift responses recorded yet. Acknowledgements and declines appear here once guards respond.
      </p>
    );
  }

  const visible = responseFilter === "all" ? rows : rows.filter((row) => row.responseStatus === responseFilter);
  const capped = rows.length >= SHIFT_RESPONSE_LOG_ROW_LIMIT;
  const toolbar = (
    <div className="schedule__toolbar">
      <label className="schedule__filter">
        <span>Response</span>
        <select value={responseFilter} onChange={(event) => setResponseFilter(event.target.value)}>
          {RESPONSE_FILTERS.map((filter) => (
            <option key={filter} value={filter}>
              {filter === "all" ? "All responses" : humanizeLabel(filter)}
            </option>
          ))}
        </select>
      </label>
      <p className="schedule__note" role="status">
        {capped ? `Showing the latest ${rows.length} responses.` : `${rows.length} latest response${rows.length === 1 ? "" : "s"}.`}
        {responseFilter !== "all" ? ` Filtered: ${visible.length} shown.` : ""}
      </p>
    </div>
  );

  if (visible.length === 0) {
    return (
      <div className="schedule schedule-responses">
        {toolbar}
        <p className="surface__state" role="status">
          No {humanizeLabel(responseFilter).toLowerCase()} shift responses in the current report.
        </p>
      </div>
    );
  }

  return (
    <div className="schedule schedule-responses">
      {toolbar}
      <ul className="schedule-responses__list">
        {visible.map((row) => (
          <li key={row.shiftResponseId} className="schedule-responses__card">
            <div className="schedule-responses__card-head">
              <span className="schedule-responses__guard">{row.guardLabel}</span>
              <span className={responseChipClass(row.responseStatus)}>{humanizeLabel(row.responseStatus)}</span>
              {row.declineReason ? (
                <span className="schedule__chip schedule__chip--at-risk">{humanizeLabel(row.declineReason)}</span>
              ) : null}
            </div>
            <dl className="schedule-responses__facts">
              <div>
                <dt>Shift</dt>
                <dd>{shiftSummary(row, timeZone)}</dd>
              </div>
              <div>
                <dt>Route · Site</dt>
                <dd>{routeSummary(row)}</dd>
              </div>
              <div>
                <dt>Responded</dt>
                <dd>
                  {row.respondedAt ? formatDisplayTime(row.respondedAt, timeZone) : "-"}
                  {row.assignedByLabel ? (
                    <span className="schedule-responses__meta">Assigned by {row.assignedByLabel}</span>
                  ) : null}
                </dd>
              </div>
              <div>
                <dt>Requirements</dt>
                <dd className="schedule__reqs">
                  {row.requiredCredentialTypes.length === 0
                    ? "-"
                    : row.requiredCredentialTypes.map((type) => (
                        <span key={type} className="schedule__chip schedule__chip--credential">
                          {humanizeLabel(type)}
                        </span>
                      ))}
                </dd>
              </div>
            </dl>
          </li>
        ))}
      </ul>
    </div>
  );
}
